import { useEffect, useState } from "react";
import { getDangerSnapshot, useDangerPlay, type DangerSnapshot } from "@/lib/dangerPlayMode";

/**
 * Dev-only Goal Protection inspector.
 * Enabled with `?debug=danger` in the URL. Shows the live pressure score,
 * xG threat, momentum, and current danger reason / window remaining so we
 * can visually validate that the predictive evaluator engages BEFORE
 * shots/goals — not after.
 */
export function DangerDebugOverlay() {
  // F1 (PR-HARDENING-01) — never run in production. The 150ms polling
  // interval and snapshot reads below are dev-only diagnostic UI.
  const isDev = import.meta.env.DEV;
  const enabled =
    isDev &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debug") === "danger";
  const danger = useDangerPlay();
  const [snap, setSnap] = useState<DangerSnapshot>(() => getDangerSnapshot());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setSnap(getDangerSnapshot()), 150);
    return () => window.clearInterval(id);
  }, [enabled]);
  if (!enabled) return null;
  const bar = (v: number) =>
    `${Math.round(Math.max(0, Math.min(1, v)) * 100)}%`;
  return (
    <div
      className="pointer-events-none fixed bottom-2 left-2 z-[9999] rounded-md border border-white/15 bg-black/80 px-3 py-2 font-mono text-[11px] leading-tight text-white shadow-lg backdrop-blur"
      aria-hidden
    >
      <div className="mb-1 flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            danger.active ? "bg-red-500 animate-pulse" : "bg-emerald-400"
          }`}
        />
        <span className="font-semibold">Goal Protection</span>
        <span className="opacity-70">{danger.active ? "ACTIVE" : "idle"}</span>
      </div>
      <div>reason: <span className="opacity-80">{snap.reason ?? "—"}</span></div>
      <div>window: <span className="opacity-80">{Math.round(snap.msRemaining)}ms</span></div>
      <div>pressure: <span className="opacity-80">{bar(snap.pressureScore)}</span></div>
      <div>xG threat: <span className="opacity-80">{bar(snap.xgThreat)}</span></div>
      <div>momentum: <span className="opacity-80">{bar(snap.momentum)}</span></div>
    </div>
  );
}